// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Dialog component for merging two contacts in the Hardfin system.
 * Provides a multi-step interface with contact selection and field comparison.
 */
import * as Sentry from '@sentry/react';
import * as z from 'zod';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { LoadingIndicator } from '@/components/navigation-layout';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  MergeContactParameters,
  mergeContact,
} from '@/contact/api/merge-contact';
import { Contact } from '@/contact/api/types';
import { ContactSelectionList } from '@/contact/components/contact-selection-list';
import { useToast } from '@/contexts/toast-context';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  alpha,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';

const mergeFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  title: z.string().nullable(),
  comments: z.string().nullable(),
  customerId: z.string().min(1, 'Company is required'),
  isArchived: z.boolean(),
});

type MergeFormData = z.infer<typeof mergeFormSchema>;

interface Props {
  isOpen: boolean;
  contact: Contact;
  onClose: () => void;
}

const steps = ['Select contact to merge', 'Select data fields to merge'];

const TOAST_DURATION_MS = 5000;
// Search field (40px) + stack spacing (16px) + fixed list height (450px),
// so the loading fallback occupies the same space as the loaded list
const SELECTION_PANEL_HEIGHT_PX = 506;
const ARCHIVED_DISPLAY_VALUE = 'Archived';
const ACTIVE_DISPLAY_VALUE = 'Active';
const RETAINED_NOT_FOUND_MESSAGE =
  'This contact no longer exists. It may have been deleted or merged by someone else. Refresh the page and try again.';
const REMOVED_NOT_FOUND_MESSAGE =
  'The selected contact no longer exists. It may have been deleted or merged by someone else. Go back and select another contact.';
const VALIDATION_ERROR_MESSAGE =
  'Some required fields are missing. Select a value for each required field.';

/**
 * Type representing valid paths for merge comparison fields.
 */
type MergeFieldPath =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'title'
  | 'comments'
  | 'customerName'
  | 'isArchived';

type FieldSource = 'primary' | 'selected';

interface FieldDefinition {
  label: string;
  path: MergeFieldPath;
  required: boolean;
  // Hide the comparison row entirely when both contacts hold the same value
  hideWhenEqual?: boolean;
}

const fields: FieldDefinition[] = [
  { label: 'First name', path: 'firstName', required: true },
  { label: 'Last name', path: 'lastName', required: true },
  { label: 'Company', path: 'customerName', required: true },
  { label: 'Email', path: 'email', required: false },
  { label: 'Phone', path: 'phone', required: false },
  { label: 'Title', path: 'title', required: false },
  { label: 'Comments', path: 'comments', required: false },
  {
    label: 'Archived',
    path: 'isArchived',
    required: false,
    hideWhenEqual: true,
  },
];

/**
 * Returns the display value for a merge field on a contact.
 */
function getFieldValue(contact: Contact, path: MergeFieldPath): string | null {
  if (path === 'customerName') {
    return contact.customer.name;
  }
  if (path === 'isArchived') {
    return contact.isArchived ? ARCHIVED_DISPLAY_VALUE : ACTIVE_DISPLAY_VALUE;
  }
  return contact[path];
}

/**
 * Returns the comparison value for a merge field on a contact.
 * Company compares by ID so two same-named companies still surface a choice.
 */
function getFieldCompareValue(
  contact: Contact,
  path: MergeFieldPath
): string | null {
  if (path === 'customerName') {
    return contact.customer.id;
  }
  return getFieldValue(contact, path);
}

function contactDisplayName(contact: Contact): string {
  return `${contact.firstName} ${contact.lastName}`;
}

interface SelectContactStepProps {
  contact: Contact;
  onContactSelect: (contact: Contact) => void;
}

function SelectContactStep(props: SelectContactStepProps) {
  return (
    <Stack spacing={3}>
      <Grid container sx={{ px: 8 }}>
        <Grid item xs={6} sx={{ display: 'flex', pr: 2 }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.2),
              }}
            >
              <Typography variant="h6">
                {contactDisplayName(props.contact)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {props.contact.email}
              </Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Current details
                </Typography>
                <Stack gap={1} direction="column" sx={{ flexGrow: 1 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {props.contact.customer.name}
                    </Typography>
                    <Typography variant="body2">company</Typography>
                  </Paper>
                  {props.contact.title && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        {props.contact.title}
                      </Typography>
                      <Typography variant="body2">title</Typography>
                    </Paper>
                  )}
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', pl: 2 }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1" gutterBottom>
              Select contact to merge with {contactDisplayName(props.contact)}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <Suspense
                fallback={
                  <Box
                    sx={{
                      height: SELECTION_PANEL_HEIGHT_PX,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <LoadingIndicator />
                  </Box>
                }
              >
                <SupportErrorBoundary message="An error occurred while loading contacts.">
                  <ContactSelectionList
                    onContactSelect={props.onContactSelect}
                    excludeContactId={props.contact.id}
                    prioritizedCustomerId={props.contact.customer.id}
                  />
                </SupportErrorBoundary>
              </Suspense>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
}

interface CompareAndMergeStepProps {
  primaryContact: Contact;
  selectedContact: Contact;
  fieldSelections: Record<string, FieldSource>;
  onFieldSelectionChange: (
    fieldPath: MergeFieldPath,
    source: FieldSource
  ) => void;
}

function CompareAndMergeStep(props: CompareAndMergeStepProps) {
  const visibleFields = fields.filter((field) => {
    if (!field.hideWhenEqual) {
      return true;
    }
    return (
      getFieldCompareValue(props.primaryContact, field.path) !==
      getFieldCompareValue(props.selectedContact, field.path)
    );
  });

  return (
    <Paper variant="outlined" sx={{ overflow: 'auto', flexGrow: 1, pb: 1 }}>
      <Stack>
        <Box
          display="grid"
          gridTemplateColumns="2fr 2fr 2fr 3fr"
          gap={2}
          sx={{ px: 2, py: 1.5 }}
          alignItems="center"
        >
          <Typography variant="subtitle2">Field</Typography>
          <Box>
            <Typography variant="subtitle2">
              {contactDisplayName(props.primaryContact)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">
              {contactDisplayName(props.selectedContact)}
            </Typography>
          </Box>
          <Typography variant="subtitle2">Merged result</Typography>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {visibleFields.map((field, index) => {
          const primaryValue = getFieldValue(props.primaryContact, field.path);
          const selectedValue = getFieldValue(
            props.selectedContact,
            field.path
          );
          const selection = props.fieldSelections[field.path] ?? 'primary';
          const mergedValue =
            selection === 'selected' ? selectedValue : primaryValue;
          // Compare by ID-backed values so same-named companies still offer
          // a choice between the two records
          const showRadio: boolean =
            (Boolean(primaryValue) || Boolean(selectedValue)) &&
            getFieldCompareValue(props.primaryContact, field.path) !==
              getFieldCompareValue(props.selectedContact, field.path);
          return (
            <Box key={field.path}>
              <Box
                display="grid"
                sx={{ px: 2 }}
                gridTemplateColumns="2fr 2fr 2fr 3fr"
                gap={2}
                alignItems="center"
              >
                <Typography variant="body2">
                  {field.label}
                  {field.required && (
                    <Typography component="span" color="error.main">
                      *
                    </Typography>
                  )}
                </Typography>

                <Box>
                  {showRadio ? (
                    <FormControlLabel
                      control={
                        <Radio
                          checked={selection === 'primary'}
                          onChange={() =>
                            props.onFieldSelectionChange(field.path, 'primary')
                          }
                        />
                      }
                      label={
                        <Typography variant="body2" component="span">
                          {primaryValue ?? (
                            <Typography component="span" color="text.secondary">
                              Not set
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  ) : primaryValue ? (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      {primaryValue}
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        Not set
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Box>
                  {showRadio ? (
                    <FormControlLabel
                      control={
                        <Radio
                          checked={selection === 'selected'}
                          onChange={() =>
                            props.onFieldSelectionChange(field.path, 'selected')
                          }
                        />
                      }
                      label={
                        <Typography variant="body2" component="span">
                          {selectedValue ?? (
                            <Typography component="span" color="text.secondary">
                              Not set
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  ) : selectedValue ? (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      {selectedValue}
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center">
                      <Box sx={{ p: 2 }}></Box>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        Not set
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Typography variant="body2" color="text.primary">
                  {mergedValue || (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      Not set
                    </Typography>
                  )}
                </Typography>
              </Box>
              {index < visibleFields.length - 1 && (
                <Divider sx={{ gridColumn: '1/-1', my: 1 }} />
              )}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export function MergeContactDialog(props: Props) {
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const { showToast } = useToast();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldSelections, setFieldSelections] = useState<
    Record<string, FieldSource>
  >({});

  const { handleSubmit, setValue } = useForm<MergeFormData>({
    resolver: zodResolver(mergeFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      firstName: props.contact.firstName,
      lastName: props.contact.lastName,
      email: props.contact.email,
      phone: props.contact.phone,
      title: props.contact.title,
      comments: props.contact.comments,
      customerId: props.contact.customer.id,
      isArchived: props.contact.isArchived,
    },
  });

  const mergeContactMutation = useMutation({
    mutationFn: (parameters: MergeContactParameters) =>
      mergeContact(props.contact.key, parameters),
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (apiError.apiStatusCode === ERROR_CODE_MAPPING.contact_not_found) {
        setErrorMessage(RETAINED_NOT_FOUND_MESSAGE);
        return;
      }
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.contact_merge_removed_not_found
      ) {
        setErrorMessage(REMOVED_NOT_FOUND_MESSAGE);
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        Sentry.captureException(error);
      }
    },
    onSuccess: () => {
      showToast({
        duration: TOAST_DURATION_MS,
        element: <Box>Merge complete, now viewing the merged record</Box>,
      });
      queryClient.invalidateQueries({ queryKey: ['contact'] });
      resetAndClose();
    },
  });

  const isUnhandledError = mergeContactMutation.isError && errorMessage === '';

  const applyFieldFromContact = (path: MergeFieldPath, contact: Contact) => {
    if (path === 'customerName') {
      setValue('customerId', contact.customer.id);
      return;
    }
    if (path === 'isArchived') {
      setValue('isArchived', contact.isArchived);
      return;
    }
    setValue(path, contact[path]);
  };

  const handleContactSelect = (contact: Contact) => {
    const initialSelections: Record<string, FieldSource> = {};
    fields.forEach((field) => {
      const primaryValue = getFieldValue(props.contact, field.path);
      const selectedValue = getFieldValue(contact, field.path);
      const source: FieldSource =
        !primaryValue && selectedValue ? 'selected' : 'primary';
      initialSelections[field.path] = source;
      applyFieldFromContact(
        field.path,
        source === 'primary' ? props.contact : contact
      );
    });
    setFieldSelections(initialSelections);
    setSelectedContact(contact);
    setActiveStep(1);
  };

  const handleFieldSelectionChange = (
    fieldPath: MergeFieldPath,
    source: FieldSource
  ) => {
    setFieldSelections((prev) => ({ ...prev, [fieldPath]: source }));

    const sourceContact =
      source === 'primary' ? props.contact : selectedContact;
    if (sourceContact) {
      applyFieldFromContact(fieldPath, sourceContact);
    }
  };

  const handleMergeSubmit = (data: MergeFormData) => {
    if (!selectedContact) {
      return;
    }
    setErrorMessage('');
    mergeContactMutation.mutate({
      removedContactID: selectedContact.id,
      firstName: data.firstName,
      lastName: data.lastName,
      customerId: data.customerId,
      email: data.email ?? null,
      phone: data.phone ?? null,
      title: data.title ?? null,
      comments: data.comments ?? null,
      isArchived: data.isArchived,
    });
  };

  const handleInvalidSubmit = () => {
    setErrorMessage(VALIDATION_ERROR_MESSAGE);
  };

  const resetAndClose = () => {
    setActiveStep(0);
    setSelectedContact(null);
    setFieldSelections({});
    setErrorMessage('');
    // Reset the mutation so a handled error cannot resurface as the generic
    // support snackbar if the dialog is mounted persistently
    mergeContactMutation.reset();
    props.onClose();
  };

  const handleClose = () => {
    if (mergeContactMutation.isPending) {
      return;
    }
    resetAndClose();
  };

  const handleBack = () => {
    setActiveStep(0);
    setSelectedContact(null);
    setErrorMessage('');
    // Clear the error state so a handled failure does not resurface as the
    // generic support snackbar once errorMessage is cleared
    mergeContactMutation.reset();
  };

  return (
    <Dialog open={props.isOpen} onClose={handleClose} maxWidth="lg" fullWidth>
      <SupportSnackbar
        isOpen={isUnhandledError}
        message="An error occurred while merging the contacts"
        onClose={mergeContactMutation.reset}
      />
      <DialogTitle>
        Merge contacts
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select another contact to merge into{' '}
          {contactDisplayName(props.contact)}
        </Typography>
      </DialogTitle>
      <Box sx={{ px: 6 }}>
        <Alert severity="warning">
          <Typography
            variant="inherit"
            component="span"
            sx={{ fontWeight: 'bold' }}
          >
            <Typography
              variant="inherit"
              component="span"
              sx={{ textDecoration: 'underline' }}
            >
              {contactDisplayName(props.contact)} will be preserved
            </Typography>{' '}
            and the
            <Typography
              variant="inherit"
              component="span"
              sx={{ textDecoration: 'underline' }}
            >
              {' '}
              other contact will be permanently deleted
            </Typography>
            .
          </Typography>{' '}
          You will select which information you want to keep (name, email, etc).
          All other associations will be migrated automatically.
        </Alert>
      </Box>
      <form onSubmit={handleSubmit(handleMergeSubmit, handleInvalidSubmit)}>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              sx={{
                '& .MuiStepIcon-root': {
                  fontSize: '2.25rem', // 1.5x the default size (1.5rem * 1.5 = 2.25rem)
                },
                '& .MuiStepIcon-text': {
                  fontWeight: 'bold',
                },
                '& .MuiStepConnector-line': {
                  borderTopWidth: '1px', // Make the line slightly thicker to match larger icons
                },
                '& .MuiStepConnector-root': {
                  top: '18px', // Adjust to align with center of enlarged icons (2.25rem / 2 = 1.125rem ≈ 18px)
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {activeStep === 0 && (
            <SelectContactStep
              contact={props.contact}
              onContactSelect={handleContactSelect}
            />
          )}

          {activeStep === 1 && selectedContact && (
            <CompareAndMergeStep
              primaryContact={props.contact}
              selectedContact={selectedContact}
              fieldSelections={fieldSelections}
              onFieldSelectionChange={handleFieldSelectionChange}
            />
          )}

          {errorMessage !== '' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', m: 1 }}>
          {activeStep === 0 ? (
            <Button onClick={handleClose}>Cancel</Button>
          ) : (
            <>
              <Button onClick={handleBack}>Back</Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={mergeContactMutation.isPending}
              >
                Confirm merge
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
